/* eslint-disable no-param-reassign */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { useMultipleSelection, useSelect } from "downshift";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import { RCL } from "../RCL";
import { Icon } from "../Icons";
import { Loader } from "../Loader";
import * as styles from "./SelectMultipleDropdown.module.scss";

const defaultLabels = {
	noResults: "No results found",
};

const SelectMultipleDropdown = React.forwardRef(
	(
		{
			id,
			loading,
			data,
			handleDynamicInputLabel,
			values,
			initialSelectedItem,
			onSelectChange,
			// isFocused,
			icon,
			environment,
			children,
			className,
			inputClassName,
			valueClassName,
			iconClassName,
			onFocus,
			onBlur,
			resetFiltersFnRef,
			handleClearAll,
			label,
			labels: providedLabels,
		},
		ref
	) => {
		const labels = { ...defaultLabels, ...providedLabels };

		const [openedViaIcon, setOpenedViaIcon] = useState(false);
		const inputReference = useRef(null);
		const iconReference = useRef(null);
		const dropdownReference = useRef(null);
		const isBrowser = typeof window !== "undefined";

		const { getDropdownProps, selectedItems, setSelectedItems } = useMultipleSelection({
			initialSelectedItems: initialSelectedItem || [],
		});

		const itemToString = item => (item ? item.label : "");

		const getDownshiftList = list =>
			list
				? list.reduce((result, section) => {
						if (id === "destinations") {
							if (section.title) {
								result.push({
									title: section.title,
									label: section.title,
									value: section.title,
								});
							}
							const filteredItems = section.items.filter(item => item.countryName !== item.label);

							result.push(...filteredItems);
						} else {
							result.push(...section.items);
						}
						return result;
				  }, [])
				: list;

		const lists = getDownshiftList(data);

		const {
			isOpen,
			closeMenu,
			selectItem,
			selectedItem,
			highlightedIndex,
			getMenuProps,
			getToggleButtonProps,
			getItemProps,
		} = useSelect({
			id,
			labelId: id,
			items: getDownshiftList(data),
			itemToString,
			stateReducer: (state, actionAndChanges) => {
				const { type, changes } = actionAndChanges;

				if (
					[
						useSelect.stateChangeTypes.ToggleButtonClick,
						useSelect.stateChangeTypes.ToggleButtonKeyDownCharacter,
						useSelect.stateChangeTypes.ToggleButtonKeyDownArrowDown,
						useSelect.stateChangeTypes.ToggleButtonKeyDownArrowUp,
					].includes(type)
				) {
					setOpenedViaIcon(false);
				}
				if (
					[
						useSelect.stateChangeTypes.ToggleButtonKeyDownEnter,
						useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton,
						useSelect.stateChangeTypes.ItemClick,
					].includes(type)
				) {
					return {
						...changes,
						isOpen: true,
						highlightedIndex: state.highlightedIndex,
					};
				}
				return changes;
			},
			onSelectedItemChange: changes => {
				onSelectChange(changes.selectedItem);
			},
			onIsOpenChange: changes => {
				const { matches: isMobile } = window.matchMedia("(max-width: 576px)");

				if (!changes.isOpen) {
					if (openedViaIcon) {
						iconReference.current.focus();
						onBlur();
					} else {
						inputReference.current.focus();
					}
				} else {
					onFocus();
					// isFocused = true;
				}

				if (changes.isOpen && isMobile && dropdownReference?.current) {
					disableBodyScroll(dropdownReference.current);
					window.scrollTo({
						top:
							dropdownReference.current.parentElement.getBoundingClientRect().top -
							16 +
							window.scrollY,
						behavior: "smooth",
					});
				} else {
					clearAllBodyScrollLocks();
				}
			},
			environment: environment
				? {
						addEventListener: environment.addEventListener.bind(environment),
						removeEventListener: environment.removeEventListener.bind(environment),
						document: environment.ownerDocument || document,
				  }
				: isBrowser && window,
		});

		const handleClick = e => {
			if (dropdownReference) {
				if (dropdownReference.current.contains(e.target)) {
					return;
				}
				if (inputReference && inputReference.current.contains(e.target)) {
					return;
				}

				closeMenu();
			}
		};

		// eslint-disable-next-line consistent-return
		const rendersList = list => {
			const titleCount = list.filter(x => x.title).length;
			if (titleCount > 1) {
				// eslint-disable-next-line no-unused-expressions, react/jsx-indent
				return (
					<ul role="group" aria-labelledby={`${id}-group`} className={styles.list}>
						{/* eslint-disable-next-line array-callback-return, consistent-return */}
						{list.map((item, index) => (
							// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions, no-unused-expressions
							<li
								{...getItemProps({
									key: index,
									item,
									index,
									className: cx(
										styles.item,
										JSON.stringify(selectedItem) === JSON.stringify(item) && styles.selected,
										item.title && styles.group,
										highlightedIndex === index && styles.highlighted,
										item.className
									),
								})}>
								{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
								<span
									className={cx(
										styles.checkbox,
										selectedItems.some(si => si.value === item.value) && styles.selected
									)}>
									<span
										className={cx(
											selectedItems.some(si => si.value === item.value) && styles.checkmark
										)}
									/>
								</span>
								{item.title ? item.title : itemToString(item)}
							</li>
						))}
					</ul>
				);
			}
			return (
				// eslint-disable-next-line no-unused-expressions, react/jsx-indent
				<ul role="group" aria-labelledby={`${id}-group`} className={styles.list}>
					{list[0].title && <span className={styles.group}>{list[0].title}</span>}
					{/* eslint-disable-next-line array-callback-return, consistent-return, arrow-body-style */}
					{list.map((item, index) => {
						if (!item.title) {
							// eslint-disable-next-line no-unused-expressions, react/jsx-indent
							return (
								<li
									{...getItemProps({
										key: index,
										item,
										index,
										className: cx(
											styles.item,
											item.value === 5 && styles.starHigh,
											JSON.stringify(selectedItem) === JSON.stringify(item) && styles.selected,
											highlightedIndex === index && styles.highlighted,
											item.className
										),
									})}>
									{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
									<span
										className={cx(
											styles.checkbox,
											selectedItems.some(si => si.value === item.value) && styles.selected
										)}>
										<span
											className={
												selectedItems.some(si => si.value === item.value) && styles.checkmark
											}
										/>
									</span>
									{itemToString(item)}
									<span className={styles.starLabel}>
										{Array.from({ length: item.value }).map((_, index) => (
											// eslint-disable-next-line react/no-array-index-key
											<Icon key={`star-${index}`} name="star" className={styles.star} />
										))}
									</span>
								</li>
							);
						}
					})}
				</ul>
			);
		};

		useEffect(() => {
			window.document.addEventListener("mousedown", handleClick, false);

			return () => {
				window.document.removeEventListener("mousedown", handleClick, false);
			};
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		useEffect(() => {
			if (initialSelectedItem) {
				selectItem(initialSelectedItem);
			}
		}, [selectItem, initialSelectedItem]);

		useEffect(() => {
			if (resetFiltersFnRef?.current !== undefined && typeof setSelectedItems === "function") {
				resetFiltersFnRef.current = () => {
					setSelectedItems([]);
				};
			}
		}, [resetFiltersFnRef, setSelectedItems]);

		useEffect(() => {
			setSelectedItems([...values]);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [values]);

		return (
			<div className={cx(className, styles.selectMultipleDropdown)} id={id}>
				<div
					ref={ref}
					className={cx(
						styles.dropdown,
						isOpen && styles.open
						// isFocused && styles.focused
					)}>
					{loading && <Loader className={styles.loader} />}
					<button
						type="button"
						aria-label={RCL({ searchKey: "a11y-open-menu" })}
						readOnly={true}
						className={cx(
							inputClassName,
							styles.input,
							isOpen && styles.open,
							selectedItems && selectedItems.length && styles.dynamicValue
						)}
						{...getDropdownProps({
							...getToggleButtonProps({
								ref: inputReference,
								onFocus: () => {
									onFocus();
									// isFocused = true;
								},
								onBlur: () => {
									if (!isOpen) {
										onBlur();
									}
								},
							}),
						})}>
						{values.length > 0 && <span className={styles.label}>{label}</span>}
						<span className={cx(valueClassName, styles.value)}>{handleDynamicInputLabel()}</span>
						{!loading && (
							<span className={cx(iconClassName, styles.icon)}>
								{icon ?? <Icon name="arrow-thick-down" className={cx(styles.iconDropdown)} />}
							</span>
						)}
					</button>
				</div>
				{children}
				<div
					{...getMenuProps({
						ref: dropdownReference,
						isOpen: true,
					})}
					className={cx(
						styles.listbox,
						id === "starRating" && styles.starRating,
						isOpen && styles.open
					)}>
					{isOpen && data.length > 0 && rendersList(lists)}
					{id === "destinations" && (
						<button
							type="button"
							className={styles.reset}
							onClick={() => {
								setSelectedItems([]);
								onSelectChange([]);
								handleClearAll();
							}}>
							<RCL searchKey="clear-all" />
						</button>
					)}
					{data.length === 0 && <span className={styles.noResults}>{labels.noResults}</span>}
				</div>
			</div>
		);
	}
);

SelectMultipleDropdown.propTypes = {
	id: PropTypes.string.isRequired,
	loading: PropTypes.bool,
	initialInputValue: PropTypes.string,
	data: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string,
			items: PropTypes.arrayOf(
				PropTypes.shape({
					label: PropTypes.string,
					value: PropTypes.string,
					countryName: PropTypes.string,
				})
			),
		})
	).isRequired,
	handleDynamicInputLabel: PropTypes.func.isRequired,
	selectedItem: PropTypes.shape({
		label: PropTypes.string,
		value: PropTypes.string,
	}),
	values: PropTypes.array.isRequired,
	initialSelectedItem: PropTypes.shape({
		label: PropTypes.string,
		value: PropTypes.string,
	}),
	onSelectChange: PropTypes.func.isRequired,
	isFocused: PropTypes.bool,
	icon: PropTypes.node,
	environment: PropTypes.any,
	children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
	className: PropTypes.string,
	inputClassName: PropTypes.string,
	valueClassName: PropTypes.string,
	iconClassName: PropTypes.string,
	onFocus: PropTypes.func,
	onBlur: PropTypes.func,
	resetFiltersFnRef: PropTypes.any,
	label: PropTypes.string.isRequired,
	labels: PropTypes.shape({
		noResults: PropTypes.string,
	}),
	handleClearAll: PropTypes.func,
};

SelectMultipleDropdown.defaultProps = {
	loading: false,
	initialInputValue: undefined,
	selectedItem: null,
	initialSelectedItem: null,
	isFocused: false,
	icon: undefined,
	environment: null,
	children: undefined,
	className: undefined,
	inputClassName: undefined,
	valueClassName: undefined,
	iconClassName: undefined,
	onFocus: () => {},
	onBlur: () => {},
	resetFiltersFnRef: undefined,
	labels: defaultLabels,
	handleClearAll: () => {},
};

export default SelectMultipleDropdown;
export { SelectMultipleDropdown };
